import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApi } from "../store.ts";

import { Contacts } from "../slices/customer/types/contacts.ts";
import {
  CreateContactRequest,
  InitResponseStateEnum,
  SessionPublicAPIsForStartFlowApi,
} from "../../orchestrator-api-client";
import { LayoutError } from "../slices/layout/types/layout-error.ts";
import { AxiosResponseHeaders } from "axios";
import { pushErrorMonitor } from "../../features/analytics/analytics.service.ts";

export type SubmitContactsArgs = {
  phone: string;
  phonePrefix: string;
  email: string;
  privacy: boolean;
};

export type SubmitContactsPayload = {
  contacts: Contacts;
  privacy: boolean;
  token: string;
  isFirstSession: boolean;
  workflowId: string;
  contactId: string;
};

export const submitContacts = createAsyncThunk<
  SubmitContactsPayload,
  SubmitContactsArgs,
  ThunkApi
>("submitContacts", async (args, { getState, rejectWithValue }) => {
  try {
    const {
      loan: { dealId },
    } = getState();

    const hutk: string = document.cookie.replace(
      /(?:(?:^|.*;\s*)hubspotutk\s*=\s*([^;]*).*$)|^.*$/,
      "$1",
    );

    const { phone, phonePrefix, email, privacy } = args;

    const createContactRequest: CreateContactRequest = {
      phone: `${phonePrefix}${phone}`,
      email,
      dealId,
      privacy,
      pageUri: window.location.href,
      pageName: "Data Collection",
      hutk,
    };

    const {
      data: { workflowId, ctOrchToken, contactId, state },
      headers,
    } = await new SessionPublicAPIsForStartFlowApi().initFlow(
      createContactRequest,
    );

    if (state) {
      if (state === InitResponseStateEnum.MultiDealSelected) {
        await new Promise<never>((resolve) => setTimeout(resolve, 2500));
        const err: LayoutError = {
          title: "La email risulta collegata ad un altro codice fiscale!",
          description: [
            "Non è possibile continuare con la richiesta di prestito in quanto collegata ad un altro codice fiscale.",
            " ",
            "Per proseguire è necessario contattare il nostro Servizio Clienti allo 0683463200 dal Lunedi al Venerdi dalle ore 9:00 alle ore 18:00.",
          ].join("\n"),
          buttonText: "Chiama l'assistenza",
          secondaryButtonText: "Annulla",
          error: state,
          onSubmit: () => window.open("tel:9971863733"),
        };
        return rejectWithValue(err);
      }
    }

    if ((headers as AxiosResponseHeaders)?.get("Location")) {
      const newLocation: string = (headers as AxiosResponseHeaders)?.get(
        "Location",
      ) as string;
      if (newLocation) {
        window.location.href = newLocation;
      }
      await new Promise<never>((resolve) => setTimeout(resolve, 2500));
      const error: LayoutError = {
        title: "Contatto esistente",
        description: "Sembra tu abbia già una pratica in corso.",
        onSubmit: () => (window.location.href = newLocation ?? "#"),
        error: `Redirect to: '${newLocation}'`,
      };

      return rejectWithValue(error);
    }

    return {
      contacts: {
        phone,
        phonePrefix,
        email,
      },
      privacy,
      token: ctOrchToken!, // TODO: Must be set as non-nullable on swagger
      isFirstSession: true,
      workflowId: workflowId!, // TODO: Must be set as non-nullable on swagger
      contactId: contactId!, // TODO: Must be set as non-nullable on swagger
    };
  } catch (err: any) {
    pushErrorMonitor(err); //send error push monitor
    const error: LayoutError = {
      title: "Creazione pratica",
      description: "Non siamo riusciti a creare la pratica.",
      error: err.message,
    };
    return rejectWithValue(error);
  }
});
